/**
 * Lazy KEV/EPSS decision support per CVE (`GET /cve/{cve}/exploitability`).
 *
 * The overview payload deliberately makes no Dependency-Track call (the KEV
 * verdict is precomputed on the cards), so EPSS is not part of it. It is fetched
 * on demand — only for the CVEs of a row the user actually expanded — and cached
 * for the lifetime of the page, so collapsing and re-expanding or an auto-refresh
 * costs nothing.
 *
 * Failing soft is the contract here: the endpoint answers 503 when
 * Dependency-Track is not configured and 502 when DT itself errors. Neither is
 * an error of the dashboard, so a missing EPSS chip is all the user sees. After
 * a 401/403/503 or a network failure the store stops asking altogether — one
 * expanded row would otherwise fire one doomed request per CVE.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import { ApiError, errorMessage, fetchExploitability, isNetworkError } from '../../lib/api';
import type { EuvdFacts, ExploitabilityFacts } from '../../lib/types';

export type ExploitabilityEntry =
  | { readonly state: 'loading' }
  | {
      readonly state: 'ready';
      /** KEV hit OR epss_score >= epss_threshold (server-side decision). */
      readonly exploitable: boolean;
      readonly inDependencyTrack: boolean;
      readonly facts: ExploitabilityFacts;
      /** ENISA EUVD enrichment; `null` when EUVD has no record or is down. */
      readonly euvd: EuvdFacts | null;
    }
  /** German reason, shown as a tooltip on a muted chip. */
  | { readonly state: 'unavailable'; readonly message: string };

export interface ExploitabilityStore {
  /** False without `triage.read` — the caller then renders no chips at all. */
  readonly enabled: boolean;
  readonly entries: ReadonlyMap<string, ExploitabilityEntry>;
  /** Idempotent: every CVE is requested at most once per page visit. */
  readonly request: (cves: readonly string[]) => void;
}

/** Keep the CMDBuild/DT round trips of one expanded row polite. */
const MAX_PARALLEL = 4;

const EMPTY: ReadonlyMap<string, ExploitabilityEntry> = new Map();

export function useExploitability(enabled: boolean): ExploitabilityStore {
  const [entries, setEntries] = useState<ReadonlyMap<string, ExploitabilityEntry>>(EMPTY);

  const queue = useRef<string[]>([]);
  const inFlight = useRef(0);
  const requested = useRef(new Set<string>());
  /** Non-null once the endpoint is known to be unusable for every CVE. */
  const stopped = useRef<string | null>(null);
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const setEntry = useCallback((cve: string, entry: ExploitabilityEntry): void => {
    if (!alive.current) return;
    setEntries((current) => {
      const next = new Map(current);
      next.set(cve, entry);
      return next;
    });
  }, []);

  // Explicitly typed because the callback references itself (drain the queue as
  // requests come back) and an inferred type would be circular.
  const pump = useCallback<() => void>(() => {
    while (inFlight.current < MAX_PARALLEL) {
      const cve = queue.current.shift();
      if (cve === undefined) return;
      inFlight.current += 1;
      void fetchExploitability(cve)
        .then((response) => {
          setEntry(cve, {
            state: 'ready',
            exploitable: response.exploitable,
            inDependencyTrack: response.in_dependency_track,
            facts: response.exploitability,
            euvd: response.euvd,
          });
        })
        .catch((error: unknown) => {
          const message = errorMessage(error);
          const fatal =
            isNetworkError(error) ||
            (error instanceof ApiError && [401, 403, 503].includes(error.status));
          if (fatal) {
            // Not a per-CVE problem: give up and answer the rest from the cache.
            stopped.current = message;
            const pending = queue.current;
            queue.current = [];
            for (const waiting of pending) setEntry(waiting, { state: 'unavailable', message });
          }
          setEntry(cve, { state: 'unavailable', message });
        })
        .finally(() => {
          inFlight.current -= 1;
          if (alive.current) pump();
        });
    }
  }, [setEntry]);

  const request = useCallback(
    (cves: readonly string[]): void => {
      if (!enabled) return;
      const fresh: string[] = [];
      for (const raw of cves) {
        const cve = raw.trim().toUpperCase();
        if (cve === '' || requested.current.has(cve)) continue;
        requested.current.add(cve);
        fresh.push(cve);
      }
      if (fresh.length === 0) return;

      const reason = stopped.current;
      if (reason !== null) {
        for (const cve of fresh) setEntry(cve, { state: 'unavailable', message: reason });
        return;
      }
      for (const cve of fresh) setEntry(cve, { state: 'loading' });
      queue.current.push(...fresh);
      pump();
    },
    [enabled, pump, setEntry],
  );

  return { enabled, entries, request };
}
